/**
 * Real welcome-screen preview.
 *
 * Renders the server-built welcome components (the same JSON the booth
 * renderer consumes) scaled into a device frame — preview IS truth, unlike
 * the old LivePreview mock. Non-interactive: the CTA is visual only.
 */

import React from 'react'
import type { WelcomeComponentDto } from '../onboardingApi'

interface WelcomePreviewProps {
  components: WelcomeComponentDto[]
  orientation: 'portrait' | 'landscape'
  backgroundColor: string
  /** CSS width of the device frame. Height derives from the canvas aspect. */
  displayWidth: number
}

const CANVAS = {
  portrait: { width: 1080, height: 1920 },
  landscape: { width: 1920, height: 1080 }
} as const

function num(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function str(value: unknown, fallback: string): string {
  return typeof value === 'string' && value ? value : fallback
}

export const WelcomePreview: React.FC<WelcomePreviewProps> = ({
  components,
  orientation,
  backgroundColor,
  displayWidth
}) => {
  const canvas = CANVAS[orientation]
  const scale = displayWidth / canvas.width
  const displayHeight = canvas.height * scale

  return (
    <div
      className="relative overflow-hidden rounded-[1.5rem] shadow-2xl ring-1 ring-black/20"
      style={{ width: displayWidth, height: displayHeight, backgroundColor }}
    >
      {components.map((c, i) => {
        const style = c.style || {}
        const geo: React.CSSProperties = {
          position: 'absolute',
          top: c.top * scale,
          left: c.left * scale,
          width: c.width * scale,
          height: c.height * scale,
          transform: c.rotation ? `rotate(${c.rotation}deg)` : undefined,
          zIndex: num(style.zIndex, 1),
          opacity: num(style.opacity, 1)
        }

        if (c.type === 'image') {
          const fullBleed = c.width >= canvas.width && c.height >= canvas.height
          return (
            <img
              key={i}
              src={c.src}
              alt={c.alt || ''}
              style={{ ...geo, objectFit: fullBleed ? 'cover' : 'contain' }}
              draggable={false}
            />
          )
        }

        if (c.type === 'text') {
          return (
            <div
              key={i}
              style={{
                ...geo,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: str(style.fontFamily, 'Inter, sans-serif'),
                fontSize: num(style.fontSize, 40) * scale,
                fontWeight: (style.fontWeight as React.CSSProperties['fontWeight']) || 'normal',
                textAlign: (style.textAlign as React.CSSProperties['textAlign']) || 'center',
                color: str(style.color, '#1D1D1D'),
                lineHeight: 1.15,
                whiteSpace: 'pre-wrap',
                overflow: 'hidden'
              }}
            >
              {c.text}
            </div>
          )
        }

        if (c.type === 'button') {
          return (
            <div
              key={i}
              style={{
                ...geo,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: str(style.fontFamily, 'Inter, sans-serif'),
                fontSize: num(style.fontSize, 40) * scale,
                fontWeight: 'bold',
                color: str(style.color, '#FFFFFF'),
                backgroundColor: str(style.backgroundColor, '#3b3b3b'),
                borderRadius: num(style.borderRadius, 43) * scale,
                boxShadow: '0 8px 24px rgba(0,0,0,0.25)'
              }}
            >
              {c.text}
            </div>
          )
        }

        return null
      })}
    </div>
  )
}

export default WelcomePreview
